import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../config';
import { getDeveloperPortalOrigin } from '../constants/externalLinks';
import {
  formatWaitTime,
  getLoginGateStatus,
  registerBlockedLogin,
  registerFailedLoginAttempt,
  registerSuccessfulLogin,
} from '../services/authSecurityService';

const ForgotPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: 20,
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.3)',
  maxWidth: 480,
  width: '100%',
  background: 'rgba(255, 255, 255, 0.03)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
}));

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const location = useLocation();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError('Enter your account email.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const gateStatus = await getLoginGateStatus(normalizedEmail);
    if (!gateStatus.allowed) {
      setError(`Too many auth attempts. Try again in ${formatWaitTime(gateStatus.waitSeconds)}.`);
      await registerBlockedLogin(normalizedEmail, {
        method: 'reset_password_request',
        route: location.pathname,
        user_agent: navigator.userAgent,
        wait_seconds: gateStatus.waitSeconds,
      });
      setLoading(false);
      return;
    }

    try {
      const origin = getDeveloperPortalOrigin();
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: `${origin}/reset-password`,
      });
      if (resetError) {
        throw resetError;
      }

      await registerSuccessfulLogin(normalizedEmail, null, {
        method: 'reset_password_request',
        route: location.pathname,
        user_agent: navigator.userAgent,
      });

      setSuccess('If an account exists for this email, a reset link has been sent.');
    } catch (err: any) {
      await registerFailedLoginAttempt(normalizedEmail, {
        method: 'reset_password_request',
        route: location.pathname,
        user_agent: navigator.userAgent,
        reason: String(err?.message || 'unknown').slice(0, 180),
      });

      const nextGate = await getLoginGateStatus(normalizedEmail);
      if (!nextGate.allowed) {
        setError(`Too many auth attempts. Try again in ${formatWaitTime(nextGate.waitSeconds)}.`);
      } else {
        setError(err.message || 'Unable to send reset email right now.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at 50% 30%, #15152a 0%, #05070a 60%)',
        padding: { xs: 2, sm: 4 },
      }}
    >
      <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <ForgotPaper elevation={0}>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1.5 }}>
            Reset Password
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mb: 3 }}>
            Enter your account email and we will send a password reset link.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={loading}
              autoComplete="email"
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
            <Button
              type="submit"
              fullWidth
              disabled={loading}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #ff8b42, #8f63ff)',
                textTransform: 'none',
                fontWeight: 600,
                py: 1.3,
                borderRadius: 2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #ff7a2f, #7a4dff)',
                },
              }}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 2.5 }}>
            Remembered your password?{' '}
            <Button component={Link} to="/login" sx={{ textTransform: 'none', color: '#ffae78', p: 0, minWidth: 0 }}>
              Back to login
            </Button>
          </Typography>
        </ForgotPaper>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;
